/**
 * Collision System - handles entity separation to prevent overlapping
 * Uses spatial hashing for efficient proximity queries
 * Optimized with adaptive iterations and reduced allocations
 */

import type { World, System } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { ColliderComponent } from '@/game/components/Collider';
import { SpatialHash, type Positioned } from '@/core/utils/SpatialHash';
import { getSurfaceY, tileHeight, isLand } from '@/rendering/terrain/TerrainGenerator';
import type { Entity } from '@/types';

interface CollidableEntity extends Positioned {
  entity: Entity;
  radius: number;
  isStatic: boolean;
  mass: number;
}

// Fast inverse square root approximation
function fastInvSqrt(x: number): number {
  return 1 / Math.sqrt(x);
}

export class CollisionSystem implements System {
  private spatialHash: SpatialHash<CollidableEntity>;
  private separationStrength: number;
  private baseMaxIterations: number;

  // Reusable arrays to reduce allocations
  private collidables: CollidableEntity[] = [];

  // Adaptive iteration thresholds
  private readonly MANY_ENTITIES_THRESHOLD = 500;
  private readonly VERY_MANY_ENTITIES_THRESHOLD = 1000;

  constructor(cellSize: number = 3, separationStrength: number = 0.8, maxIterations: number = 3) {
    this.spatialHash = new SpatialHash<CollidableEntity>(cellSize);
    this.separationStrength = separationStrength;
    this.baseMaxIterations = maxIterations;
  }

  update(world: World, deltaTime: number): void {
    // Rebuild spatial hash each frame with all collidable entities
    this.spatialHash.clear();

    const entities = world.query([ComponentTypes.Position, ComponentTypes.Collider]);

    // Reuse collidables array
    this.collidables.length = 0;
    const collidables = this.collidables;

    // Collect all collidable entities
    for (let i = 0, len = entities.length; i < len; i++) {
      const entity = entities[i];
      const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const collider = world.getComponent<ColliderComponent>(entity, ComponentTypes.Collider)!;

      const collidable: CollidableEntity = {
        entity,
        x: position.x,
        z: position.z,
        radius: collider.radius,
        isStatic: collider.isStatic,
        mass: collider.mass,
      };

      collidables.push(collidable);
      this.spatialHash.insert(collidable);
    }

    // Adaptive iterations based on entity count
    let maxIterations = this.baseMaxIterations;
    const entityCount = collidables.length;
    if (entityCount > this.VERY_MANY_ENTITIES_THRESHOLD) {
      maxIterations = 1; // Minimal iterations for very large counts
    } else if (entityCount > this.MANY_ENTITIES_THRESHOLD) {
      maxIterations = 2; // Reduced iterations for large counts
    }

    // Perform multiple iterations for better separation
    for (let iteration = 0; iteration < maxIterations; iteration++) {
      // Process each non-static entity
      for (let i = 0, len = collidables.length; i < len; i++) {
        const collidable = collidables[i];
        if (collidable.isStatic) continue;

        const position = world.getComponent<PositionComponent>(collidable.entity, ComponentTypes.Position)!;

        // Query nearby entities
        const queryRadius = collidable.radius * 3;
        const nearby = this.spatialHash.queryRadius(collidable.x, collidable.z, queryRadius);

        let separationX = 0;
        let separationZ = 0;

        for (let j = 0, jLen = nearby.length; j < jLen; j++) {
          const other = nearby[j];
          if (other.entity === collidable.entity) continue;

          const dx = collidable.x - other.x;
          const dz = collidable.z - other.z;
          const distSq = dx * dx + dz * dz;
          const minDist = collidable.radius + other.radius;
          const minDistSq = minDist * minDist;

          // Check if overlapping
          if (distSq < minDistSq && distSq > 0.0001) {
            // Only compute sqrt when actually needed
            const invDist = fastInvSqrt(distSq);
            const dist = distSq * invDist; // dist = distSq / sqrt(distSq) = sqrt(distSq)
            const overlap = minDist - dist;

            // Normalize direction using invDist
            const nx = dx * invDist;
            const nz = dz * invDist;

            // Calculate push amount based on mass ratio
            let pushRatio: number;
            if (other.isStatic) {
              pushRatio = 1.0;
            } else {
              pushRatio = other.mass / (collidable.mass + other.mass);
            }

            // Apply separation force
            const pushAmount = overlap * pushRatio * this.separationStrength;
            separationX += nx * pushAmount;
            separationZ += nz * pushAmount;
          } else if (distSq < 0.0001) {
            // Entities at same position - push in random direction
            const angle = Math.random() * Math.PI * 2;
            const pushAmount = minDist * 0.5;
            separationX += Math.cos(angle) * pushAmount;
            separationZ += Math.sin(angle) * pushAmount;
          }
        }

        // Apply separation
        if (separationX !== 0 || separationZ !== 0) {
          // Limit maximum separation per frame to avoid jittering
          const maxSep = collidable.radius * deltaTime * 10;
          const sepMagSq = separationX * separationX + separationZ * separationZ;

          if (sepMagSq > maxSep * maxSep) {
            const invMag = fastInvSqrt(sepMagSq);
            separationX *= invMag * maxSep;
            separationZ *= invMag * maxSep;
          }

          // Check if new position would be in water
          const newX = position.x + separationX;
          const newZ = position.z + separationZ;
          const newHeight = tileHeight(newX, newZ);

          // Only apply separation if target is land
          if (isLand(newHeight)) {
            position.x = newX;
            position.z = newZ;
            position.y = getSurfaceY(newX, newZ);

            // Update spatial hash position for next iteration
            const oldX = collidable.x;
            const oldZ = collidable.z;
            collidable.x = position.x;
            collidable.z = position.z;
            this.spatialHash.update(collidable, oldX, oldZ);
          }
          // If water, don't apply separation - unit stays in place
        }
      }
    }
  }
}
